<template>
  <div class="shared-accounts-view">
    <!-- Sektions-Header -->
    <div class="section-header">
      <div class="header-title">
        <v-icon color="success" size="28" class="mr-3">mdi-account-multiple</v-icon>
        <h1>Sammelaccounts</h1>
      </div>
      <div class="header-actions">
        <v-btn
          icon
          variant="text"
          color="success"
          class="refresh-btn"
          @click="fetchSharedAccounts"
          :loading="loading"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <v-divider class="admin-divider"></v-divider>

    <!-- Info-Banner -->
    <v-alert
      type="info"
      variant="tonal"
      class="mb-4"
      icon="mdi-information-outline"
    >
      <div class="alert-content">
        <strong>Sammelaccounts</strong> können von mehreren Mitarbeitern gleichzeitig genutzt werden.
        Jede Session hat einen eigenen Status und kann individuell verwaltet werden.
      </div>
    </v-alert>

    <!-- Statistiken -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card">
          <v-card-text class="pa-4">
            <div class="stat-label">Sammelaccounts</div>
            <div class="stat-value">{{ sharedAccountsCount }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card">
          <v-card-text class="pa-4">
            <div class="stat-label">Aktive Sessions</div>
            <div class="stat-value">{{ totalActiveSessions }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card">
          <v-card-text class="pa-4">
            <div class="stat-label">Normale Accounts</div>
            <div class="stat-value">{{ normalAccountsCount }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card">
          <v-card-text class="pa-4">
            <div class="stat-label">Gesamt-Benutzer</div>
            <div class="stat-value">{{ totalUsers }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Benutzer-Tabelle -->
    <v-card class="users-table-card">
      <v-card-title class="px-4 py-3">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Benutzer suchen"
          variant="outlined"
          density="comfortable"
          hide-details
          clearable
          class="search-field"
        ></v-text-field>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="users"
        :search="search"
        :loading="loading"
        class="shared-accounts-table"
        item-value="id"
      >
        <!-- Loginname -->
        <template v-slot:[`item.loginname`]="{ item }">
          <div class="user-info">
            <v-icon
              :color="item.is_shared_account ? 'success' : 'grey'"
              size="20"
              class="mr-2"
            >
              {{ item.is_shared_account ? 'mdi-account-multiple' : 'mdi-account' }}
            </v-icon>
            <span class="font-weight-medium">{{ item.loginname }}</span>
          </div>
        </template>

        <!-- Name -->
        <template v-slot:[`item.name`]="{ item }">
          {{ item.vorname }} {{ item.nachname }}
        </template>

        <!-- Account-Typ -->
        <template v-slot:[`item.is_shared_account`]="{ item }">
          <v-chip
            :color="item.is_shared_account ? 'success' : 'grey'"
            size="small"
            variant="flat"
          >
            <v-icon start size="16">
              {{ item.is_shared_account ? 'mdi-account-multiple' : 'mdi-account' }}
            </v-icon>
            {{ item.is_shared_account ? 'Sammelaccount' : 'Normal' }}
          </v-chip>
        </template>

        <!-- Aktive Sessions -->
        <template v-slot:[`item.active_sessions`]="{ item }">
          <v-chip
            :color="item.active_sessions > 0 ? 'primary' : 'grey'"
            size="small"
            variant="outlined"
          >
            {{ item.active_sessions }} {{ item.active_sessions === 1 ? 'Session' : 'Sessions' }}
          </v-chip>
        </template>

        <!-- Status -->
        <template v-slot:[`item.user_status`]="{ item }">
          <v-chip
            :color="getStatusColor(item.user_status)"
            size="small"
            variant="flat"
          >
            {{ getStatusText(item.user_status) }}
          </v-chip>
        </template>

        <!-- Aktionen -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="action-buttons">
            <v-tooltip text="Account-Typ umschalten" location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  size="small"
                  variant="text"
                  :color="item.is_shared_account ? 'error' : 'success'"
                  @click="toggleSharedAccount(item)"
                  :loading="item.toggling"
                >
                  <v-icon size="20">
                    {{ item.is_shared_account ? 'mdi-account-convert' : 'mdi-account-multiple-plus' }}
                  </v-icon>
                </v-btn>
              </template>
            </v-tooltip>

            <v-tooltip text="Sessions anzeigen" location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  size="small"
                  variant="text"
                  color="primary"
                  @click="showSessionsDialog(item)"
                  :disabled="item.active_sessions === 0"
                >
                  <v-icon size="20">mdi-monitor-eye</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Sessions-Dialog -->
    <v-dialog
      v-model="sessionsDialog"
      max-width="900"
      scrollable
    >
      <v-card>
        <v-card-title class="d-flex align-center pa-4">
          <v-icon color="primary" class="mr-3">mdi-monitor-multiple</v-icon>
          <div>
            <div class="text-h6">Aktive Sessions</div>
            <div class="text-caption text-medium-emphasis" v-if="selectedUser">
              {{ selectedUser.vorname }} {{ selectedUser.nachname }} ({{ selectedUser.loginname }})
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon variant="text" @click="sessionsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-4" style="max-height: 500px;">
          <div v-if="loadingSessions" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <p class="mt-3 text-medium-emphasis">Sessions werden geladen...</p>
          </div>

          <div v-else-if="sessions.length === 0" class="text-center py-8">
            <v-icon size="64" color="grey">mdi-monitor-off</v-icon>
            <p class="mt-3 text-medium-emphasis">Keine aktiven Sessions</p>
          </div>

          <div v-else class="sessions-list">
            <v-card
              v-for="session in sessions"
              :key="session.id"
              class="session-card mb-3"
              variant="outlined"
            >
              <v-card-text class="pa-4">
                <div class="d-flex align-items-start">
                  <div class="flex-grow-1">
                    <!-- Anzeigename / Display Name -->
                    <div class="d-flex align-items-center mb-2">
                      <v-icon color="primary" size="20" class="mr-2">mdi-account-circle</v-icon>
                      <span class="text-subtitle-1 font-weight-medium">
                        {{ session.session_display_name || 'Unbenannt' }}
                      </span>
                      <v-chip
                        v-if="session.session_status"
                        :color="getStatusColor(session.session_status)"
                        size="small"
                        class="ml-2"
                      >
                        {{ getStatusText(session.session_status) }}
                      </v-chip>
                    </div>

                    <!-- Status-Nachricht -->
                    <div v-if="session.session_custom_message" class="mb-2">
                      <v-icon size="16" class="mr-1">mdi-message-text</v-icon>
                      <span class="text-caption">{{ session.session_custom_message }}</span>
                    </div>

                    <!-- Session-Informationen -->
                    <div class="session-info">
                      <div class="info-item">
                        <v-icon size="16" class="mr-1">mdi-devices</v-icon>
                        <span class="text-caption">{{ getDeviceName(session.device_info) }}</span>
                      </div>
                      <div class="info-item">
                        <v-icon size="16" class="mr-1">mdi-ip-network</v-icon>
                        <span class="text-caption">{{ session.ip_address }}</span>
                      </div>
                      <div class="info-item">
                        <v-icon size="16" class="mr-1">mdi-clock-outline</v-icon>
                        <span class="text-caption">{{ formatDateTime(session.last_activity) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Aktionen -->
                  <div class="ml-3">
                    <v-tooltip text="Session beenden" location="top">
                      <template #activator="{ props }">
                        <v-btn
                          v-bind="props"
                          icon
                          size="small"
                          variant="text"
                          color="error"
                          @click="terminateSession(session)"
                        >
                          <v-icon>mdi-close-circle</v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="elevated"
            @click="sessionsDialog = false"
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar für Benachrichtigungen -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      location="top"
    >
      {{ snackbar.text }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">
          Schließen
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

// Daten
const users = ref([]);
const sessions = ref([]);
const search = ref('');
const loading = ref(false);
const loadingSessions = ref(false);
const sessionsDialog = ref(false);
const selectedUser = ref(null);

const snackbar = ref({
  show: false,
  text: '',
  color: 'success'
});

// Tabellen-Header
const headers = [
  { title: 'Loginname', key: 'loginname', sortable: true },
  { title: 'Name', key: 'name', sortable: true },
  { title: 'E-Mail', key: 'email', sortable: true },
  { title: 'Typ', key: 'is_shared_account', sortable: true, align: 'center' },
  { title: 'Aktive Sessions', key: 'active_sessions', sortable: true, align: 'center' },
  { title: 'Status', key: 'user_status', sortable: true, align: 'center' },
  { title: 'Aktionen', key: 'actions', sortable: false, align: 'center' }
];

// Berechnete Werte
const sharedAccountsCount = computed(() => {
  return users.value.filter(u => u.is_shared_account).length;
});

const normalAccountsCount = computed(() => {
  return users.value.filter(u => !u.is_shared_account).length;
});

const totalActiveSessions = computed(() => {
  return users.value.reduce((sum, u) => sum + (u.active_sessions || 0), 0);
});

const totalUsers = computed(() => {
  return users.value.length;
});

// Methoden
async function fetchSharedAccounts() {
  loading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}admin/shared_accounts.php`, {
      params: { action: 'list' },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      users.value = response.data.users.map(user => ({
        ...user,
        toggling: false,
        // Boolean-Konvertierung
        is_shared_account: user.is_shared_account === 1 || user.is_shared_account === "1" || user.is_shared_account === true
      }));
    } else {
      showSnackbar(response.data.message || 'Fehler beim Laden der Benutzer', 'error');
    }
  } catch (error) {
    showSnackbar('Fehler beim Laden der Benutzer', 'error');
  } finally {
    loading.value = false;
  }
}

async function toggleSharedAccount(user) {
  user.toggling = true;
  try {
    const response = await axios.post(`${API_BASE_URL}admin/shared_accounts.php`, {
      action: 'toggle',
      user_id: user.id,
      is_shared: !user.is_shared_account
    }, {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' }
    });

    if (response.data.status === 'success') {
      user.is_shared_account = response.data.is_shared_account === 1 || response.data.is_shared_account === "1" || response.data.is_shared_account === true;
      showSnackbar(response.data.message, 'success');
    } else {
      showSnackbar(response.data.message || 'Fehler beim Umschalten', 'error');
    }
  } catch (error) {
    showSnackbar('Fehler beim Umschalten des Account-Typs', 'error');
  } finally {
    user.toggling = false;
  }
}

async function showSessionsDialog(user) {
  selectedUser.value = user;
  sessionsDialog.value = true;
  loadingSessions.value = true;

  try {
    const response = await axios.get(`${API_BASE_URL}admin/shared_accounts.php`, {
      params: {
        action: 'get_details',
        user_id: user.id
      },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      sessions.value = response.data.sessions || [];
    } else {
      showSnackbar(response.data.message || 'Fehler beim Laden der Sessions', 'error');
      sessions.value = [];
    }
  } catch (error) {
    showSnackbar('Fehler beim Laden der Sessions', 'error');
    sessions.value = [];
  } finally {
    loadingSessions.value = false;
  }
}

async function terminateSession(session) {
  if (!confirm('Möchten Sie diese Session wirklich beenden?')) {
    return;
  }

  try {
    const response = await axios.post(`${API_BASE_URL}admin/user_sessions.php`, {
      action: 'terminate',
      session_id: session.id
    }, {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' }
    });

    if (response.data.status === 'success') {
      showSnackbar('Session wurde beendet', 'success');
      // Session aus der Liste entfernen
      sessions.value = sessions.value.filter(s => s.id !== session.id);
      // Session-Count aktualisieren
      if (selectedUser.value) {
        selectedUser.value.active_sessions--;
      }
    } else {
      showSnackbar(response.data.message || 'Fehler beim Beenden der Session', 'error');
    }
  } catch (error) {
    showSnackbar('Fehler beim Beenden der Session', 'error');
  }
}

function getStatusColor(status) {
  const colors = {
    online: 'success',
    away: 'warning',
    busy: 'error',
    offline: 'grey'
  };
  return colors[status] || 'grey';
}

function getStatusText(status) {
  const texts = {
    online: 'Online',
    away: 'Abwesend',
    busy: 'Beschäftigt',
    offline: 'Offline'
  };
  return texts[status] || status || 'Unbekannt';
}

function getDeviceName(userAgent) {
  if (!userAgent) return 'Unbekannt';

  if (userAgent.includes('Chrome')) return 'Chrome Browser';
  if (userAgent.includes('Firefox')) return 'Firefox Browser';
  if (userAgent.includes('Safari')) return 'Safari Browser';
  if (userAgent.includes('Edge')) return 'Edge Browser';

  return 'Browser';
}

function formatDateTime(dateTime) {
  if (!dateTime) return '-';

  const date = new Date(dateTime);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000); // Sekunden

  if (diff < 60) return 'Gerade eben';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;

  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function showSnackbar(text, color = 'success') {
  snackbar.value = {
    show: true,
    text,
    color
  };
}

// Lifecycle
onMounted(() => {
  fetchSharedAccounts();
});
</script>

<style scoped>
.shared-accounts-view {
  padding: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-title {
  display: flex;
  align-items: center;
}

.header-title h1 {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.admin-divider {
  margin-bottom: 24px;
  opacity: 0.1;
}

.alert-content {
  font-size: 14px;
}

.stat-card {
  background: rgba(30, 30, 40, 0.5);
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.stat-label {
  font-size: 12px;
  text-transform: uppercase;
  opacity: 0.7;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #4caf50;
}

.users-table-card {
  background: rgba(30, 30, 40, 0.5);
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.search-field {
  max-width: 400px;
}

.user-info {
  display: flex;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

.sessions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.session-card {
  background: rgba(30, 30, 40, 0.3);
  border: 1px solid rgba(76, 175, 80, 0.2);
  transition: all 0.2s ease;
}

.session-card:hover {
  border-color: rgba(76, 175, 80, 0.4);
  transform: translateY(-2px);
}

.session-info {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
}

.info-item {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.7);
}

@media (max-width: 600px) {
  .shared-accounts-view {
    padding: 16px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .search-field {
    max-width: 100%;
  }
}
</style>
